import { z } from 'zod';
import { ProfileAnalysisStructureSchema } from './profile-analysis.js';
import { PROFILE_VERIFIED_TYPES } from './profile-constants.js';
import { booleanFromQueryParam } from './shared/query.js';

export { PROFILE_VERIFIED_TYPES } from './profile-constants.js';

export const ProfileRelationshipFlagsSchema = z.object({
  isFollowing: z.boolean().optional(),
  followsYou: z.boolean().optional(),
});

export const ProfileAffiliationSchema = z.object({
  organizationProfileId: z.string(),
  organizationName: z.string().nullable().optional(),
  organizationUsername: z.string().nullable().optional(),
  badgeUrl: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
});

export const ProfileResponseSchema = z
  .object({
    id: z.string(),
    username: z.string(),
    name: z.string(),
    profileImageUrl: z.string().nullable(),
    profileBannerUrl: z.string().nullable().optional(),
    description: z.string().nullable(),
    descriptionExpanded: z.string().nullable().optional(),
    location: z.string().nullable(),
    url: z.string().nullable(),
    verifiedType: z.enum(PROFILE_VERIFIED_TYPES),
    followersCount: z.number(),
    followingCount: z.number(),
    tweetCount: z.number(),
    totalInteractions: z.number().nullable().optional(),
    listedCount: z.number(),
    createdAt: z.date().nullable(),
    lastFetchedAt: z.date().nullable(),
    updatedAt: z.date().nullable(),
    affiliations: z.array(ProfileAffiliationSchema).optional(),
    primaryAffiliation: ProfileAffiliationSchema.nullable().optional(),
    entities: z.record(z.string(), z.any()).optional(),
  })
  .merge(ProfileRelationshipFlagsSchema);

export type ProfileResponse = z.infer<typeof ProfileResponseSchema>;

export const ProfileSummarySchema = ProfileResponseSchema.pick({
  id: true,
  username: true,
  name: true,
  profileImageUrl: true,
}).extend({
  verifiedType: z.enum(PROFILE_VERIFIED_TYPES).optional(),
  verified: z.boolean().optional(),
});

export type ProfileSummary = z.infer<typeof ProfileSummarySchema>;

export const ProfileStatusTweetStatsSchema = z.object({
  totalInDatabase: z.number(),
  dateRange: z
    .object({
      earliest: z.string().nullable(),
      latest: z.string().nullable(),
    })
    .nullable(),
  lastSyncedAt: z.string().nullable(),
  needsSync: z.boolean(),
});

export type ProfileStatusTweetStats = z.infer<typeof ProfileStatusTweetStatsSchema>;

export const ProfileStatusResponseSchema = z.object({
  profile: ProfileResponseSchema.nullable(),
  tweetStats: ProfileStatusTweetStatsSchema,
  isAuthenticated: z.boolean(),
  isStale: z.boolean().optional(),
});

export type ProfileStatusResponse = z.infer<typeof ProfileStatusResponseSchema>;

export const ProfileLookupQuerySchema = z
  .object({
    profileId: z.string().min(1).optional(),
    username: z.string().min(1).optional(),
  })
  .refine((value) => Boolean(value.profileId || value.username), {
    message: 'profileId or username query parameter is required',
    path: ['profileId'],
  });

export type ProfileLookupQuery = z.infer<typeof ProfileLookupQuerySchema>;

export const ProfileActionMutationSchema = z.object({
  action: z.enum(['follow', 'unfollow', 'mute', 'unmute']),
});

export const ProfileFollowActionSchema = ProfileActionMutationSchema.pick({ action: true }).extend({
  action: z.enum(['follow', 'unfollow']),
});

export const ProfileMuteActionSchema = ProfileActionMutationSchema.pick({ action: true }).extend({
  action: z.enum(['mute', 'unmute']),
});

export const ProfileIdPathParamsSchema = z.object({
  profileId: z.string().min(1),
});
export type ProfileIdPathParams = z.infer<typeof ProfileIdPathParamsSchema>;

export const ProfileHashtagExamplePathParamsSchema = z.object({
  profileId: z.string(),
  tag: z.string().min(1),
});
export type ProfileHashtagExamplePathParams = z.infer<typeof ProfileHashtagExamplePathParamsSchema>;

export const ProfileRefreshRequestSchema = z.undefined();
export type ProfileRefreshRequest = z.infer<typeof ProfileRefreshRequestSchema>;

export const ProfileActionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const ProfileListSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  ownerId: z.string().nullable().optional(),
  ownerName: z.string().nullable().optional(),
  ownerUsername: z.string().nullable().optional(),
  ownerProfileImageUrl: z.string().nullable().optional(),
  memberCount: z.number().int().nonnegative().optional(),
  followerCount: z.number().int().nonnegative().optional(),
  isPrivate: z.boolean().optional(),
  createdAt: z.date().nullable().optional(),
});

export const ProfileListsQuerySchema = z.object({
  refresh: z.coerce.boolean().optional(),
});

export const ProfileListsResponseSchema = z.object({
  lists: z.array(ProfileListSchema),
  count: z.number().int().nonnegative(),
  ownedLists: z.array(ProfileListSchema).optional(),
  ownedCount: z.number().int().nonnegative().optional(),
  cached: z.boolean().optional(),
  note: z.string().optional(),
});

export const ProfileInsightsQuerySchema = z.object({
  timezone: z.string().optional(),
});

export const ProfileInsightsResponseSchema = z.object({
  timeOfDay: z.array(
    z.object({
      hour: z.number().int().min(0).max(23),
      count: z.number().int().min(0),
      original: z.number().int().min(0),
      links: z.number().int().min(0),
      replies: z.number().int().min(0),
      quotes: z.number().int().min(0),
    })
  ),
  tweetTypes: z.object({
    original: z.number().int().min(0),
    links: z.number().int().min(0),
    replies: z.number().int().min(0),
    quotes: z.number().int().min(0),
  }),
  threadFrequency: z.object({
    threadCount: z.number().int().min(0),
    totalTweets: z.number().int().min(0),
    percentage: z.number().min(0),
  }),
  selfHype: z.object({
    selfRetweetCount: z.number().int().min(0),
    totalTweets: z.number().int().min(0),
    percentage: z.number().min(0),
  }),
  replyNetwork: z.array(
    z.object({
      username: z.string(),
      name: z.string(),
      profileImageUrl: z.string().url().optional(),
      count: z.number().int().min(0),
    })
  ),
  peakDays: z.array(z.object({ day: z.string(), count: z.number().int().min(0) })),
  topHashtags: z.array(z.object({ tag: z.string(), count: z.number().int().min(0) })),
  interactionSummary: z.object({
    replyInbound: z.number().int().min(0),
    replyOutbound: z.number().int().min(0),
    retweetInbound: z.number().int().min(0),
    retweetOutbound: z.number().int().min(0),
    mentionInbound: z.number().int().min(0),
    mentionOutbound: z.number().int().min(0),
    totalInbound: z.number().int().min(0),
    totalOutbound: z.number().int().min(0),
    reciprocity: z.number().min(0).nullable(),
    lastInboundAt: z.date().nullable(),
    lastOutboundAt: z.date().nullable(),
  }),
});

export const ProfileTimezoneResponseSchema = z.object({
  timezone: z.string(),
});

export const ProfileTimezoneUpdateSchema = z.object({
  timezone: z.string().min(1),
});

export const ProfileTimezoneUpdateResponseSchema = z.object({
  success: z.literal(true),
  timezone: z.string(),
});

export const ProfileRefreshResponseSchema = z.object({
  success: z.boolean().optional(),
  error: z.string().optional(),
  profileId: z.string().optional(),
  skipped: z.boolean().optional(),
  refreshing: z.boolean().optional(),
});

export const ProfileAnalysisCacheResponseSchema = z.object({
  analysis: ProfileAnalysisStructureSchema.nullable(),
  updatedAt: z.date().nullable(),
});

export const ProfileAnalysisCacheSaveSchema = z.object({
  analysis: ProfileAnalysisStructureSchema,
});

export const ProfileAnalysisCacheSaveResponseSchema = z.object({
  success: z.literal(true),
  updatedAt: z.date(),
  analysisId: z.string(),
});

export const ProfileSyncStatusResponseSchema = z.object({
  progress: z.number(),
  completed: z.boolean(),
  error: z.string().optional(),
  mode: z.string().optional(),
});

export const ProfileHashtagExampleResponseSchema = z.object({
  tweetId: z.string().nullable(),
});

export const ProfileTweetsFormatSchema = z.enum(['flat', 'normalized']);

export const ProfileTweetsQuerySchema = z.object({
  format: ProfileTweetsFormatSchema.optional(),
  page: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  sortBy: z.enum(['latest', 'engagement']).default('latest'),
  excludeReplies: booleanFromQueryParam.default(false),
  excludeRetweets: booleanFromQueryParam.default(false),
});

export const ProfileTweetsResponseSchema = z
  .object({
    format: ProfileTweetsFormatSchema.default('flat'),
  })
  .passthrough();

export const ProfileReplyNetworkEntrySchema = z.object({
  username: z.string(),
  name: z.string(),
  profileImageUrl: z.string().url().optional(),
  count: z.number().int().min(0),
});

export const ProfileReplyNetworkResponseSchema = z.array(ProfileReplyNetworkEntrySchema);

export const ProfileSearchItemSchema = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string(),
  profileImageUrl: z.string().nullable(),
  verifiedType: z.enum(PROFILE_VERIFIED_TYPES),
  followersCount: z.number().int().nonnegative(),
  followingCount: z.number().int().nonnegative(),
  tweetCount: z.number().int().nonnegative(),
  listedCount: z.number().int().nonnegative(),
  description: z.string().optional(),
  createdAt: z.date().optional(),
});
export type ProfileSearchItem = z.infer<typeof ProfileSearchItemSchema>;

export const ProfileSearchResponseSchema = z.object({
  users: z.array(ProfileSearchItemSchema),
});

export const ProfileSearchQuerySchema = z
  .object({
    q: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(25).optional(),
    source: z.enum(['database', 'twitter']).optional(),
  })
  .strip();

export const ProfileImageVariantSchema = z.enum(['mini', 'normal', 'bigger', 'original', '200x200', '400x400']);

export const ProfileImageRequestSchema = z
  .object({
    profileRef: z.string().trim().min(1),
    variant: ProfileImageVariantSchema.optional(),
  })
  .strip();

export const ProfileImageResponseSchema = z
  .object({
    status: z.number().int().default(302),
    location: z.string().url(),
    cacheControl: z.string(),
    vary: z.string().optional(),
    profileId: z.string(),
    username: z.string().nullable(),
    variant: ProfileImageVariantSchema,
    lastFetchedAt: z.date().nullable(),
  })
  .strip();

export const ProfileImageQuerySchema = z
  .object({
    variant: ProfileImageVariantSchema.optional(),
  })
  .strip();

export const ProfileSyncRequestSchema = z
  .object({
    profileId: z.string().trim().optional(),
    username: z.string().trim().optional(),
    maxTweets: z.number().int().min(1).max(3200).default(1000),
    excludeReplies: z.boolean().optional().default(false),
    excludeRetweets: z.boolean().optional().default(false),
    includeRelatedProfiles: z.boolean().optional(),
    checkExisting: z.boolean().optional(),
    sinceId: z.string().optional(),
    mode: z.enum(['full', 'incremental', 'auto']).default('auto'),
  })
  .refine((value) => value.profileId || value.username, {
    message: 'profileId or username is required',
    path: ['profileId'],
  });

export const ProfileSyncFetchSummarySchema = z.object({
  id: z.string(),
  dateRange: z.object({
    earliest: z.date().nullable(),
    latest: z.date().nullable(),
  }),
  tweetCount: z.number().nullable(),
  fetchedAt: z.date(),
  metadata: z.record(z.string(), z.any()),
});

export const ProfileSyncStatsSchema = z.object({
  totalTweets: z.number().int().nonnegative(),
  latestTweetAt: z.date().nullable(),
  earliestTweetAt: z.date().nullable(),
});

export const ProfileSyncResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('started'),
    message: z.string(),
    fetchId: z.string(),
    profileId: z.string(),
    username: z.string().nullable(),
    mode: z.enum(['full', 'incremental']),
    alreadyRunning: z.literal(false),
    currentStats: ProfileSyncStatsSchema,
    recentFetches: z.array(ProfileSyncFetchSummarySchema).optional(),
    currentFetch: z
      .object({
        id: z.string(),
        completed: z.boolean(),
        tweetCount: z.number().nullable(),
        dateRange: z.object({
          earliest: z.date().nullable(),
          latest: z.date().nullable(),
        }),
      })
      .nullable()
      .optional(),
  }),
  z.object({
    status: z.literal('already_running'),
    error: z.string(),
    profileId: z.string(),
    username: z.string().nullable(),
    mode: z.enum(['full', 'incremental']),
    recentFetch: z.object({
      id: z.string(),
      createdAt: z.date(),
      tweetCount: z.number().nullable(),
    }),
    currentStats: ProfileSyncStatsSchema,
    recentFetches: z.array(ProfileSyncFetchSummarySchema).optional(),
    currentFetch: z
      .object({
        id: z.string(),
        completed: z.boolean(),
        tweetCount: z.number().nullable(),
        dateRange: z.object({
          earliest: z.date().nullable(),
          latest: z.date().nullable(),
        }),
      })
      .nullable()
      .optional(),
  }),
]);

export const ProfileSyncStatusQuerySchema = z.object({
  profileId: z.string().trim().optional(),
  username: z.string().trim().optional(),
  fetchId: z.string().trim().optional(),
});

export const ProfileSyncSnapshotResponseSchema = z.object({
  profileId: z.string(),
  username: z.string().nullable(),
  currentStats: ProfileSyncStatsSchema,
  recentFetches: z.array(ProfileSyncFetchSummarySchema),
  currentFetch: z
    .object({
      id: z.string(),
      completed: z.boolean(),
      tweetCount: z.number().nullable(),
      dateRange: z.object({
        earliest: z.date().nullable(),
        latest: z.date().nullable(),
      }),
    })
    .nullable(),
});
